import { Popper } from '@material-ui/core'
import useBrandingConfig from 'branding/useBrandingConfig'
import { orgSelectors } from 'ducks/orgs'
import SupplierLogoWithFallback from 'elements/SupplierLogoWithFallback'
import { ActionType, EventType, SellerIntegrationType, useTrackComponent } from 'opensolar-common'
import { Box, styled, Typography } from 'opensolar-ui'
import useEnabledHardwareSuppliers, { manualCostIntegration } from 'pages/ordering/hooks/useEnabledHardwareSuppliers'
import { useGetSupplierConfig } from 'pages/ordering/hooks/useGetSupplierConfig'
import { CostSource } from 'pages/ordering/type'
import { useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useFeatureFlag } from 'util/featureFlags'
import { InverterWizardComponentType } from '../inverterWizard/types'
import { DistributorDataTypeV2, HardwareComponentType } from '../types'

type ComponentType = HardwareComponentType | InverterWizardComponentType

interface DistributorChipSelectorProps {
  distributors: DistributorDataTypeV2[]
  componentActivationCost: number
  costSource: CostSource | undefined
  onClickDistributor: (costSource: CostSource) => void
  component: ComponentType
}

const Chip = styled(Box, { name: 'Chip' })<{ isSelected?: boolean }>(({ isSelected }) => ({
  border: isSelected ? '2px solid #4272DD' : '1px solid rgb(0, 0, 0, 0.3)',
  borderRadius: '4px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 38,
  height: 20,
  padding: 4,
}))

const ExpandChip = styled(Chip, { name: 'ExpandChip' })(() => ({
  width: 38,
  height: 20,
}))

const PopperContainer = styled('div')(() => ({
  width: '240px',
  height: '88px',
  backgroundColor: '#fff',
  padding: '8px',
  borderRadius: '8px',
  overflow: 'auto',
  zIndex: 1400,
  boxShadow: '0px 8px 24px 0px rgba(0, 0, 0, 0.15), 0px 2px 8px 0px rgba(0, 0, 0, 0.1)',
  border: '1px solid rgba(0, 0, 0, 0.08)',
}))

const DistributorChip = ({
  supplier,
  onClick,
  isSelected = false,
  component,
}: {
  supplier: {
    logo: string | URL | undefined
    price: number
    source: CostSource
  }
  onClick: (costSource: CostSource) => void
  isSelected?: boolean
  component: ComponentType
}) => {
  const { getSupplierConfig } = useGetSupplierConfig()
  const supplierConfig: SellerIntegrationType | null = getSupplierConfig(supplier?.source)
  const org = useSelector(orgSelectors.getOrg)
  const orgName = component.org_name || org?.name || ''
  const title = supplierConfig?.name || orgName || ''
  const chipImageUrl = (supplier.source === 'component' && !supplier?.logo
    ? org?.logo_public_url
    : supplier?.logo) as string

  return (
    <Chip onClick={() => onClick(supplier?.source)} isSelected={isSelected}>
      <SupplierLogoWithFallback logoUrl={chipImageUrl} name={title} />
    </Chip>
  )
}

const getSourceChipAmplitudeContext = (costSource: CostSource) => {
  switch (costSource) {
    case 'manual':
      return 'override'
    case 'component':
      return 'manual pricing'
    default:
      return costSource
  }
}

const DistributorChipSelector = ({
  distributors,
  componentActivationCost,
  costSource,
  onClickDistributor,
  component,
}: DistributorChipSelectorProps) => {
  const { getSupplierConfig } = useGetSupplierConfig()
  const [supplierAnchorEl, setSupplierAnchorEl] = useState<HTMLElement | null>(null)
  const orgUrl = component.org_logo_public_url || ''
  const brandingConfig = useBrandingConfig()
  const previousCostSource = useRef<string | undefined>(undefined)
  const { filteredSuppliers: enabledDistributors } = useEnabledHardwareSuppliers(true)
  const hasPreference = (useSelector(orgSelectors.getOrgSupplierPreferences)?.length ?? 0) > 0
  const proHardwareSupplierPreferenceEnabled = useFeatureFlag('supplier_preference_settings', 'on')

  const logoUrl = useMemo(() => {
    if (orgUrl) {
      return orgUrl
    } else if (brandingConfig.homeOverride?.placeholderLogo) {
      return `${window.PUBLIC_URL}/images/${brandingConfig.homeOverride.placeholderLogo}`
    }
  }, [orgUrl, brandingConfig])

  const { trackEvent } = useTrackComponent({
    componentKey: 'source_chip',
    eventName: 'Component Selector Activity',
  })

  const handleClickDistributor = (costSource: CostSource) => {
    setSupplierAnchorEl(null)
    onClickDistributor(costSource)

    const amplitudeAction =
      !!previousCostSource.current && costSource !== previousCostSource.current ? ActionType.CHANGE : ActionType.CLICK
    const context = getSourceChipAmplitudeContext(costSource)
    previousCostSource.current = costSource
    trackEvent(EventType.USER_INTERACTION, { type: amplitudeAction }, { context })
  }

  const sortedDistributors = useMemo(() => {
    const manualPricingItem = {
      logo: logoUrl,
      price: componentActivationCost,
      source: 'component' as CostSource,
      filterKey: 'component',
    }

    if (distributors.length === 0) return [manualPricingItem]

    const distributorItems = enabledDistributors
      .filter((supplier) =>
        distributors.some((d) => d.distributor === supplier || supplier === manualCostIntegration.filterKey)
      )
      .map((supplierKey) => {
        if (supplierKey === manualCostIntegration.filterKey) return manualPricingItem

        const supplierConfig = getSupplierConfig(supplierKey)
        const distributor = distributors.find((d) => d.distributor === supplierKey)
        if (!distributor) return null
        return {
          filterKey: supplierKey,
          logo: supplierConfig?.logoUrl,
          price: distributor.price,
          source: supplierConfig?.filterKey as CostSource,
        }
      })
      .filter((item) => item !== null)

    if (!hasPreference || !proHardwareSupplierPreferenceEnabled) {
      return distributorItems.sort((a, b) => a.price - b.price)
    }

    return distributorItems
  }, [distributors, componentActivationCost, enabledDistributors, logoUrl, getSupplierConfig, hasPreference, proHardwareSupplierPreferenceEnabled])

  return (
    <>
      {sortedDistributors.slice(0, 5).map((distributor, index) => {
        return (
          <DistributorChip
            key={`${distributor.source}-${index}`}
            supplier={distributor}
            onClick={handleClickDistributor}
            isSelected={costSource === distributor.source}
            component={component}
          />
        )
      })}
      {sortedDistributors.length > 5 && (
        <ExpandChip onClick={(e) => setSupplierAnchorEl(!supplierAnchorEl ? e.currentTarget : null)}>
          <Typography>{`+${sortedDistributors.length - 5}`}</Typography>
        </ExpandChip>
      )}
      <Popper
        open={Boolean(supplierAnchorEl)}
        anchorEl={supplierAnchorEl}
        style={{ zIndex: 2000 }}
        placement="top"
        modifiers={{
          offset: {
            enabled: true,
            offset: '0,12',
          },
          preventOverflow: {
            enabled: true,
            boundariesElement: 'viewport',
          },
        }}
      >
        <PopperContainer>
          <Box alignItems="center" display="flex" flexDirection="row" flexWrap="wrap" gridGap={8}>
            {sortedDistributors.map((distributor, index) => {
              return (
                <DistributorChip
                  key={`${distributor.source}-${index}-expanded`}
                  supplier={distributor}
                  onClick={handleClickDistributor}
                  isSelected={costSource === distributor.source}
                  component={component}
                />
              )
            })}
          </Box>
        </PopperContainer>
      </Popper>
    </>
  )
}

export default DistributorChipSelector
